'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthProvider';
import { useOrganization } from './OrganizationProvider';
import {
  type AppNotification,
  type NotificationFilter,
  fetchLiveNotifications,
  apiMarkNotificationAsRead,
  apiMarkAllNotificationsAsRead,
  apiClearReadNotifications,
  apiDismissNotification,
  NOTIFICATIONS_REFRESH_EVENT,
} from '@/lib/notifications';

interface NotificationContextValue {
  notifications: AppNotification[];
  filteredNotifications: AppNotification[];
  unreadCount: number;
  totalCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  activeFilter: NotificationFilter;
  setActiveFilter: (filter: NotificationFilter) => void;
  tabCounts: {
    all: number;
    unread: number;
    approval: number;
    book: number;
    alert: number;
  };
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  dismissNotification: (id: string) => void;
  clearReadNotifications: () => void;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

const PAGE_SIZE = 20;

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user, status } = useAuth();
  const { currentOrganizationId, organizations } = useOrganization();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [serverUnreadCount, setServerUnreadCount] = useState<number>(0);
  const [activeFilter, setActiveFilter] = useState<NotificationFilter>('all');
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (status !== 'authenticated' || !user) {
      if (isMountedRef.current) {
        setNotifications([]);
        setLoading(false);
        setHasMore(false);
        setTotalCount(0);
        setServerUnreadCount(0);
      }
      return;
    }

    try {
      const result = await fetchLiveNotifications({
        user,
        organizationId: currentOrganizationId,
        organizations,
        page: 1,
        limit: PAGE_SIZE,
      });

      if (isMountedRef.current) {
        setNotifications(result.notifications);
        setPage(1);
        setHasMore(result.pagination.has_more);
        setTotalCount(result.pagination.total);
        setServerUnreadCount(result.unreadCount);
      }
    } catch (err) {
      console.error('Failed to fetch dynamic notifications', err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [status, user, currentOrganizationId, organizations]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || status !== 'authenticated' || !user) {
      return;
    }

    setLoadingMore(true);
    const nextPage = page + 1;

    try {
      const result = await fetchLiveNotifications({
        user,
        organizationId: currentOrganizationId,
        organizations,
        page: nextPage,
        limit: PAGE_SIZE,
      });

      if (isMountedRef.current) {
        // Append unique notifications
        setNotifications((prev) => {
          const existingIds = new Set(prev.map((n) => n.id));
          const newItems = result.notifications.filter((n) => !existingIds.has(n.id));
          return [...prev, ...newItems];
        });
        setPage(nextPage);
        setHasMore(result.pagination.has_more);
        setTotalCount(result.pagination.total);
        setServerUnreadCount(result.unreadCount);
      }
    } catch (err) {
      console.error('Failed to load more notifications', err);
    } finally {
      if (isMountedRef.current) {
        setLoadingMore(false);
      }
    }
  }, [loadingMore, hasMore, status, user, page, currentOrganizationId, organizations]);

  // Initial and reactive refresh on auth/organization switch
  useEffect(() => {
    if (status === 'authenticated' && user) {
      setLoading(true);
      void refresh();
    } else {
      setNotifications([]);
      setLoading(false);
      setHasMore(false);
      setTotalCount(0);
      setServerUnreadCount(0);
    }
  }, [status, user, currentOrganizationId, organizations, refresh]);

  // Polling interval (30 seconds)
  useEffect(() => {
    if (status !== 'authenticated' || !user) return;

    const interval = setInterval(() => {
      void refresh();
    }, 30000);

    return () => clearInterval(interval);
  }, [status, user, refresh]);

  // Revalidate on window focus & custom refresh events
  useEffect(() => {
    const handleFocus = () => {
      if (status === 'authenticated' && user) {
        void refresh();
      }
    };

    const handleCustomRefresh = () => {
      void refresh();
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, handleCustomRefresh);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, handleCustomRefresh);
    };
  }, [status, user, refresh]);

  const markAsRead = useCallback(
    (id: string) => {
      if (!user) return;
      void apiMarkNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      setServerUnreadCount((prev) => Math.max(0, prev - 1));
    },
    [user]
  );

  const markAllAsRead = useCallback(() => {
    if (!user) return;
    void apiMarkAllNotificationsAsRead(currentOrganizationId);
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setServerUnreadCount(0);
  }, [user, currentOrganizationId]);

  const dismissNotification = useCallback(
    (id: string) => {
      if (!user) return;
      const target = notifications.find((n) => n.id === id);
      void apiDismissNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setTotalCount((prev) => Math.max(0, prev - 1));
      if (target && !target.isRead) {
        setServerUnreadCount((prev) => Math.max(0, prev - 1));
      }
    },
    [user, notifications]
  );

  const clearReadNotifications = useCallback(() => {
    if (!user) return;
    void apiClearReadNotifications(currentOrganizationId);
    setNotifications((prev) => prev.filter((n) => !n.isRead));
    setTotalCount((prev) => prev - notifications.filter((n) => n.isRead).length);
  }, [user, currentOrganizationId, notifications]);

  const unreadCount = useMemo(() => {
    return Math.max(
      serverUnreadCount,
      notifications.filter((n) => !n.isRead).length
    );
  }, [serverUnreadCount, notifications]);

  const tabCounts = useMemo(() => {
    const counts = {
      all: totalCount || notifications.length,
      unread: unreadCount,
      approval: notifications.filter((n) => n.category === 'approval').length,
      book: notifications.filter((n) => n.category === 'book').length,
      alert: notifications.filter((n) => n.category === 'security' || n.category === 'system').length,
    };
    return counts;
  }, [totalCount, notifications, unreadCount]);

  const filteredNotifications = useMemo(() => {
    switch (activeFilter) {
      case 'unread':
        return notifications.filter((n) => !n.isRead);
      case 'approval':
        return notifications.filter((n) => n.category === 'approval');
      case 'book':
        return notifications.filter((n) => n.category === 'book');
      case 'alert':
        return notifications.filter((n) => n.category === 'security' || n.category === 'system');
      case 'all':
      default:
        return notifications;
    }
  }, [notifications, activeFilter]);

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      filteredNotifications,
      unreadCount,
      totalCount,
      loading,
      loadingMore,
      hasMore,
      activeFilter,
      setActiveFilter,
      tabCounts,
      markAsRead,
      markAllAsRead,
      dismissNotification,
      clearReadNotifications,
      refresh,
      loadMore,
    }),
    [
      notifications,
      filteredNotifications,
      unreadCount,
      totalCount,
      loading,
      loadingMore,
      hasMore,
      activeFilter,
      tabCounts,
      markAsRead,
      markAllAsRead,
      dismissNotification,
      clearReadNotifications,
      refresh,
      loadMore,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
